import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import type { Design, Widget, WidgetType } from '@noorview/core';
import { ASPECTS, GAPS, MARGINS, clampRect, cornerRadius, createWidget, resetWidget, gridMetrics, keepShape, minSize, pxToCell, rectToPx, resizeRect, sizeFor, WIDGETS, type Handle, type Rect } from '@noorview/core';
import type { Stage } from '@noorview/display';
import { useApp, toast, useScreenBroadcasts } from '../data/store';
import { openSheet, useMedia, useStudioPanels } from '../app/ui';
import { Icon } from '../ui/icons';
import { IconButton, Popover, Segmented, Switch, confirm } from '../ui/controls';
import { StageView } from '../ui/StageView';
import { useStudio } from './store';
import { ContrastNotice } from './Contrast';
import { useParams } from 'react-router';
import { canSwap, LiveAnnouncements, useCanvasAnnouncements } from '../sheets/Announce';
import { getClip, setPicking, togglePicked, useClipboard } from './clipboard';
import { copyAll, copySelected, pasteHere, pasteTitle } from './clipActions';

/* Library → canvas drag, shared between the two panels. */
export const useDnd = create<{ type: WidgetType | null; x: number; y: number; dropAt: ((type: WidgetType, x: number, y: number) => boolean) | null }>(() => ({ type: null, x: 0, y: 0, dropAt: null }));

const marginKey = (m: number) => (m <= (MARGINS.small + MARGINS.medium) / 2 ? 'small' : m >= (MARGINS.medium + MARGINS.large) / 2 ? 'large' : 'medium');
const gapKey = (g: number) => (Object.entries(GAPS) as Array<[keyof typeof GAPS, number]>).reduce((a, b) => (Math.abs(b[1] - g) < Math.abs(a[1] - g) ? b : a))[0];
const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
/** The modifier key people press on this computer. */
const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? '⌘' : 'Ctrl';

/** Zoom is relative to "Fit" (100% = the whole screen fits). You can't zoom out past half of that. */
const MIN_ZOOM = 0.5, MAX_ZOOM = 4;
const clampZoom = (z: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

export type Tool = 'select' | 'hand';
export const useCanvasTool = create<{ tool: Tool; spaceHand: boolean; set: (t: Tool) => void }>((set) => ({ tool: 'select', spaceHand: false, set: (tool) => set({ tool }) }));

export function Canvas() {
  const ws = useApp((s) => s.ws)!;
  const design = useStudio((s) => s.design)!;
  const change = useStudio((s) => s.change);
  const select = useStudio((s) => s.select);
  const tool = useCanvasTool((s) => (s.spaceHand ? 'hand' : s.tool));
  const setTool = useCanvasTool((s) => s.set);
  const [zoom, setZoomState] = useState(1);
  const [dots, setDots] = useState(() => { try { return localStorage.getItem('noorview.studio.dots') !== '0'; } catch { return true; } });
  const optRef = useRef<HTMLButtonElement>(null);
  const [opts, setOpts] = useState(false);
  const copyRef = useRef<HTMLButtonElement>(null);
  const [copyMenu, setCopyMenu] = useState(false);
  const clip = useClipboard((s) => s.clip);
  const picking = useClipboard((s) => s.picking);
  const picked = useClipboard((s) => s.picked);
  useEffect(() => { getClip(); return () => setPicking(false); }, []);
  const board = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState({ w: 600, h: 340 });
  const [panning, setPanning] = useState(false);
  // Where to keep still while zooming: a point on the screen and its position within the stage (0–1).
  const anchor = useRef<{ cx: number; cy: number; fx: number; fy: number } | 'center' | null>(null);

  useLayoutEffect(() => {
    const el = board.current!;
    const ro = new ResizeObserver(() => setAvail({ w: el.clientWidth - 48, h: el.clientHeight - 96 }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const m = gridMetrics(design);
  const fit = Math.max(0.05, Math.min(avail.w / m.width, avail.h / m.height));
  const fill = Math.max(avail.w / m.width, (avail.h + 72) / m.height) / fit;
  const hostW = Math.round(m.width * fit * zoom), hostH = Math.round(m.height * fit * zoom);

  const stageEl = () => board.current?.querySelector<HTMLElement>('.editor-stage') ?? null;
  const zoomTo = useCallback((z: number, at?: { x: number; y: number }) => {
    const el = stageEl();
    if (at && el) {
      const r = el.getBoundingClientRect();
      anchor.current = { cx: at.x, cy: at.y, fx: (at.x - r.left) / r.width, fy: (at.y - r.top) / r.height };
    } else anchor.current = 'center';
    setZoomState((old) => { const n = clampZoom(z); return Math.abs(n - old) < 1e-4 ? old : n; });
  }, []);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  // After a zoom, scroll so the point under the pointer stays put (or re-centre for Fit and Fill).
  useLayoutEffect(() => {
    const a = anchor.current, b = board.current, el = stageEl();
    anchor.current = null;
    if (!a || !b || !el) return;
    if (a === 'center') { b.scrollLeft = (b.scrollWidth - b.clientWidth) / 2; b.scrollTop = (b.scrollHeight - b.clientHeight) / 2; return; }
    const r = el.getBoundingClientRect();
    b.scrollLeft += r.left + a.fx * r.width - a.cx;
    b.scrollTop += r.top + a.fy * r.height - a.cy;
  }, [zoom]);

  // Ctrl (or ⌘) + scroll zooms around the pointer; a trackpad pinch sends the same thing. Plain scrolling pans.
  useEffect(() => {
    const b = board.current!;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      zoomTo(zoomRef.current * Math.exp(-Math.max(-60, Math.min(60, dy)) * 0.0028), { x: e.clientX, y: e.clientY });
    };
    b.addEventListener('wheel', onWheel, { passive: false });
    return () => b.removeEventListener('wheel', onWheel);
  }, [zoomTo]);

  // Keyboard: V select, H hand, hold Space for the hand, Ctrl/⌘ + / − / 0 to zoom.
  useEffect(() => {
    const typing = (e: KeyboardEvent) => !!(e.target as HTMLElement).closest?.('input,textarea,select,[contenteditable="true"]') || !!document.querySelector('.scrim');
    const down = (e: KeyboardEvent) => {
      if (typing(e)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === '=' || e.key === '+')) { e.preventDefault(); zoomTo(zoomRef.current * 1.25); return; }
      if (mod && e.key === '-') { e.preventDefault(); zoomTo(zoomRef.current / 1.25); return; }
      if (mod && e.key === '0') { e.preventDefault(); zoomTo(1); return; }
      if (mod || e.altKey) return;
      if (e.key === 'v' || e.key === 'V') useCanvasTool.getState().set('select');
      else if (e.key === 'h' || e.key === 'H') useCanvasTool.getState().set('hand');
      else if (e.key === ' ' && !e.repeat) {
        // Only when nothing else wants the space bar (a focused button would be pressed by it).
        const a = document.activeElement;
        if (a && a !== document.body && !board.current?.contains(a)) return;
        e.preventDefault();
        useCanvasTool.setState({ spaceHand: true });
      }
    };
    const up = (e: KeyboardEvent) => { if (e.key === ' ') useCanvasTool.setState({ spaceHand: false }); };
    const blur = () => useCanvasTool.setState({ spaceHand: false });
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); window.removeEventListener('blur', blur); };
  }, [zoomTo]);

  // Hand tool (or the middle mouse button): drag to move around the canvas.
  const pan = useRef<{ x: number; y: number; sl: number; st: number; id: number } | null>(null);
  const onBoardDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const b = board.current!;
    if (tool === 'hand' || e.button === 1) {
      e.preventDefault();
      pan.current = { x: e.clientX, y: e.clientY, sl: b.scrollLeft, st: b.scrollTop, id: e.pointerId };
      b.setPointerCapture(e.pointerId);
      setPanning(true);
      return;
    }
    if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('pb-inner')) select(null);
  };
  const onBoardMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = pan.current;
    if (!p || p.id !== e.pointerId) return;
    const b = board.current!;
    b.scrollLeft = p.sl - (e.clientX - p.x);
    b.scrollTop = p.st - (e.clientY - p.y);
  };
  const onBoardUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pan.current?.id !== e.pointerId) return;
    pan.current = null;
    setPanning(false);
  };

  const libDrawer = useMedia('(max-width: 1320px)');
  const inspDrawer = useMedia('(max-width: 1040px)');
  const panels = useStudioPanels();
  const selectedId = useStudio((s) => s.selected);
  const selectedW = design.widgets.find((w) => w.id === selectedId) ?? null;
  const { screenId } = useParams();
  // What the screen is showing on top of the design right now (announcements, appeals), unless hidden to edit underneath.
  const liveBcs = useScreenBroadcasts(screenId);
  const annHidden = useCanvasAnnouncements((s) => s.hidden);
  const screenName = ws.screens.find((s) => s.id === screenId)?.name ?? 'this screen';
  // Any widget can become an announcement for a while, except the ones the congregation always needs.
  const canAnnounce = !!selectedW && canSwap(selectedW, design.widgets);
  const announce = () => { if (screenId) openSheet({ name: 'announce', screenId, widgetId: canAnnounce ? selectedW!.id : undefined }); };
  const removeSelected = () => {
    if (!selectedW || selectedW.locked) return;
    change((d) => ({ ...d, widgets: d.widgets.filter((w) => w.id !== selectedW.id) }));
    select(null);
    toast(`${WIDGETS[selectedW.type].label} deleted`, { action: { label: 'Undo', run: () => useStudio.getState().undo() } });
  };
  const resetSelected = () => {
    if (!selectedW || selectedW.locked) return;
    change((d) => ({ ...d, widgets: d.widgets.map((w) => (w.id === selectedW.id ? resetWidget(w, d.widgets) : w)) }));
    toast(`${WIDGETS[selectedW.type].label} reset`, { action: { label: 'Undo', run: () => useStudio.getState().undo() } });
  };
  const clear = async () => {
    if (!design.widgets.length) return;
    if (!(await confirm('Clear the canvas?', { body: 'This removes every widget from this screen’s draft. You can undo it straight away.', ok: 'Clear', danger: true }))) return;
    change((d) => ({ ...d, widgets: [] }));
    select(null);
    toast('Canvas cleared', { action: { label: 'Undo', run: () => useStudio.getState().undo() } });
  };

  return (
    <section className="card glass canvas-card" aria-label="Canvas">
      <div className="grid-controls">
        {libDrawer && <button type="button" className="chip-btn panel-btn" onClick={() => panels.set({ library: !panels.library, inspector: false })} aria-expanded={panels.library}><Icon name="layers" size={18} />Widgets</button>}
        <span className="gc-info mono" title="Every widget snaps to these cells">{ASPECTS[design.aspect].label} · {design.cols} × {design.rows} grid</span>
        <span className="spacer" />
        {picking ? (
          <>
            <span className="pick-count" role="status">{picked.length ? `${picked.length} selected` : 'Click widgets to select them'}</span>
            <button type="button" className="chip-btn" onClick={() => copySelected(screenName)} disabled={!picked.length}><Icon name="copy" size={18} />Copy selected</button>
            <button type="button" className="chip-btn" onClick={() => setPicking(false)}><Icon name="check" size={18} />Done</button>
          </>
        ) : (
          <>
            {screenId && <button type="button" className="chip-btn" onClick={announce} aria-label={canAnnounce ? 'Switch to announcement' : 'Announcement'} title={canAnnounce ? 'Show an announcement in this widget for a while' : selectedW ? `This is the screen’s only ${WIDGETS[selectedW.type].label.toLowerCase()}, so it stays as it is. You can still show an announcement elsewhere on the screen.` : 'Show an announcement on this screen for a while'}><Icon name="megaphone" size={18} /><span className="lbl">{canAnnounce ? 'Switch to announcement' : 'Announcement'}</span></button>}
            <button ref={copyRef} type="button" className="chip-btn c1" onClick={() => setCopyMenu((v) => !v)} aria-expanded={copyMenu} aria-haspopup="menu" title={`Copy widgets (${MOD} C) to paste here or on another screen`} aria-label="Copy"><Icon name="copy" size={18} /><span className="lbl">Copy</span></button>
            <button type="button" className="chip-btn c1" onClick={() => pasteHere(screenName)} disabled={!clip} title={`${pasteTitle()}${clip ? ` (${MOD} V)` : ''}`} aria-label="Paste"><Icon name="paste" size={18} /><span className="lbl">Paste</span></button>
            <button type="button" className="chip-btn c1" onClick={resetSelected} disabled={!selectedW || selectedW.locked} title="Remove this widget’s own colours, text options, size and settings (its content stays)" aria-label="Reset widget"><Icon name="refresh" size={18} /><span className="lbl">Reset widget</span></button>
            <button type="button" className="chip-btn danger" onClick={removeSelected} disabled={!selectedW || selectedW.locked} title={selectedW?.locked ? 'Unlock it in Layers to delete' : 'Delete the selected widget (⌫)'} aria-label="Delete"><Icon name="trash" size={18} /><span className="lbl">Delete</span></button>
            <button type="button" className="chip-btn" onClick={clear} disabled={!design.widgets.length} title="Clear the canvas" aria-label="Clear"><Icon name="clear" size={18} /><span className="lbl">Clear</span></button>
          </>
        )}
        <Popover anchor={copyRef.current} open={copyMenu} onClose={() => setCopyMenu(false)} width={260} label="Copy">
          <div className="menu" role="menu">
            <button type="button" role="menuitem" disabled={!selectedW} onClick={() => { setCopyMenu(false); copySelected(screenName); }}><Icon name="copy" size={18} />Copy selected<span className="spacer" /><span className="hint mono">{MOD} C</span></button>
            <button type="button" role="menuitem" disabled={!design.widgets.length} onClick={() => { setCopyMenu(false); copyAll(screenName); }}><Icon name="layers" size={18} />Copy all widgets</button>
            <button type="button" role="menuitem" disabled={!design.widgets.length} onClick={() => { setCopyMenu(false); setPicking(true, selectedW ? [selectedW.id] : []); select(null); }}><Icon name="pick" size={18} />Select widgets<span className="spacer" /><span className="hint">Shift-click</span></button>
          </div>
        </Popover>
        <IconButton ref={optRef} icon="sliders" label="Grid options" size="sm" onClick={() => setOpts((v) => !v)} aria-expanded={opts} />
        {inspDrawer && <button type="button" className="chip-btn panel-btn" onClick={() => panels.set({ inspector: !panels.inspector, library: false })} aria-expanded={panels.inspector}><Icon name="sliders" size={18} />{selectedW ? 'Edit widget' : 'Screen'}</button>}
        <Popover anchor={optRef.current} open={opts} onClose={() => setOpts(false)} width={340} label="Grid options">
          <div className="stack">
            <div className="section-label">Screen border</div>
            <Segmented size="sm" label="Screen border" value={marginKey(design.margin)} onChange={(v) => change((d) => ({ ...d, margin: MARGINS[v as keyof typeof MARGINS] }))}
              options={[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }]} />
            <p className="hint" style={{ marginTop: -6 }}>A clean border all the way round, so widgets never touch the edge of the TV.</p>
            <div className="section-label">Spacing between widgets</div>
            <Segmented size="sm" label="Spacing" value={gapKey(design.gap)} onChange={(v) => change((d) => ({ ...d, gap: GAPS[v as keyof typeof GAPS] }))}
              options={[{ value: 'none', label: 'None' }, { value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }]} />
            <div className="row"><span className="label">Show grid dots</span><span className="spacer" /><Switch label="Show grid dots" checked={dots} onChange={(v) => { setDots(v); try { localStorage.setItem('noorview.studio.dots', v ? '1' : '0'); } catch { /* ignore */ } }} /></div>
            <p className="hint">Every widget snaps to the {design.cols} × {design.rows} grid. Each screen shape has its own fixed grid, so layouts always line up.</p>
          </div>
        </Popover>
      </div>
      {screenId && <LiveAnnouncements screenId={screenId} />}
      <ContrastNotice />
      <div className="pb-wrap">
      <div className={`pasteboard tool-${tool}${panning ? ' panning' : ''}`} ref={board} onPointerDown={onBoardDown} onPointerMove={onBoardMove} onPointerUp={onBoardUp} onPointerCancel={onBoardUp}
        onAuxClick={(e) => { if (e.button === 1) e.preventDefault(); }}>
        <div className="pb-inner" style={{ width: Math.max(hostW + 48, avail.w + 48), height: Math.max(hostH + 96, avail.h + 96) }}>
          <EditorStage design={design} width={hostW} height={hostH} dots={dots} masjid={ws.masjid} timetable={ws.timetable} screenName={ws.screens.find((x) => x.id === screenId)?.name ?? ''} broadcasts={annHidden ? undefined : liveBcs} />
        </div>
      </div>
      <div className="zoom glass" role="toolbar" aria-label="Canvas tools">
        <button type="button" className={tool === 'select' ? 'on' : ''} aria-pressed={tool === 'select'} data-tip={'Select and move widgets\nShortcut: V'} aria-label="Select tool" onClick={() => setTool('select')}><Icon name="cursor" size={18} /></button>
        <button type="button" className={tool === 'hand' ? 'on' : ''} aria-pressed={tool === 'hand'} data-tip={'Drag to move around the canvas\nWidgets stay put. Shortcut: H, or hold Space'} aria-label="Hand tool" onClick={() => setTool('hand')}><Icon name="hand" size={18} /></button>
        <i className="div" />
        <output className="mono" tabIndex={0} data-tip={`Hold ${MOD} and scroll to zoom\n${MOD} + and ${MOD} − also zoom`}>{Math.round(zoom * 100)}%</output>
        <i className="div" />
        <button type="button" className={`fit${Math.abs(zoom - 1) < 0.01 ? ' on' : ''}`} onClick={() => zoomTo(1)} data-tip={`Show the whole screen\nShortcut: ${MOD} 0`}>Fit</button>
        <button type="button" className={`fit${Math.abs(zoom - clampZoom(fill)) < 0.01 ? ' on' : ''}`} onClick={() => zoomTo(fill)} data-tip="Fill the canvas area">Fill</button>
      </div>
      </div>
    </section>
  );
}

/* ---------- the stage with its editing overlay ---------- */

function EditorStage({ design, width, height, dots, masjid, timetable, screenName, broadcasts }: { design: Design; width: number; height: number; dots: boolean; masjid: import('@noorview/core').Masjid; timetable: import('@noorview/core').TimetableDay[]; screenName: string; broadcasts?: readonly import('@noorview/core').Broadcast[] }) {
  const stageRef = useRef<Stage | null>(null);
  const [overlay, setOverlay] = useState<HTMLElement | null>(null);
  const [scale, setScale] = useState(1);
  const onStage = useCallback((s: Stage | null) => { stageRef.current = s; setOverlay(s?.overlay ?? null); if (s) setScale(s.scale); }, []);
  return (
    <div className="editor-stage" style={{ width, height }}>
      <StageView onStage={onStage} design={design} masjid={masjid} timetable={timetable} mode="editor" screenName={screenName} broadcasts={broadcasts} className="stage-fill" onScale={(s) => setScale(s.scale)} label="Screen canvas" />
      {overlay && createPortal(<Overlay design={design} scale={scale} dots={dots} stage={stageRef} />, overlay)}
    </div>
  );
}

interface DragState { kind: 'move' | 'resize'; id: string; handle?: Handle; sx: number; sy: number; start: Rect; moved: boolean }

function Overlay({ design, scale, dots, stage }: { design: Design; scale: number; dots: boolean; stage: React.RefObject<Stage | null> }) {
  const selected = useStudio((s) => s.selected);
  const select = useStudio((s) => s.select);
  const dnd = useDnd();
  const drag = useRef<DragState | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const m = gridMetrics(design);
  const inv = 1 / Math.max(scale, 0.01);
  const stepX = m.cellW + m.gap, stepY = m.cellH + m.gap;

  // Converts a pointer position to display pixels.
  const toDisplay = useCallback((cx: number, cy: number) => {
    const el = stage.current?.stage;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: (cx - r.left) / scale, y: (cy - r.top) / scale, inside: cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom };
  }, [scale, stage]);

  // Dropping a widget from the library.
  const ghost = (() => {
    if (!dnd.type) return null;
    const p = toDisplay(dnd.x, dnd.y);
    if (!p?.inside) return null;
    const s = sizeFor(dnd.type, design.cols, design.rows);
    const c = pxToCell(p.x, p.y, m, design.cols, design.rows);
    return clampRect({ x: c.x - Math.floor((s.w - 1) / 2), y: c.y - Math.floor((s.h - 1) / 2), w: s.w, h: s.h }, design.cols, design.rows, { w: s.minW, h: s.minH });
  })();
  const ghostRef = useRef(ghost);
  ghostRef.current = ghost;
  useEffect(() => {
    useDnd.setState({
      dropAt: (type) => {
        const g = ghostRef.current;
        if (!g) return false;
        const w = createWidget(type, g);
        Object.assign(w, keepShape(w, g, useStudio.getState().design!, 'h'));
        useStudio.getState().change((d) => ({ ...d, widgets: [...d.widgets, w] }));
        useStudio.getState().select(w.id);
        if (type === 'logo' && !useApp.getState().ws?.masjid.logo) openSheet({ name: 'logo' });
        return true;
      },
    });
    return () => useDnd.setState({ dropAt: null });
  }, []);

  const picking = useClipboard((s) => s.picking);
  const picked = useClipboard((s) => s.picked);
  const onDown = (e: React.PointerEvent, w: Widget, handle?: Handle) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    // Picking several widgets to copy: click (or shift-click at any time) adds or removes one.
    if (!handle && (picking || e.shiftKey)) {
      const cur = useStudio.getState().selected;
      if (!picking) setPicking(true, cur && cur !== w.id ? [cur] : []);
      togglePicked(w.id);
      select(null);
      return;
    }
    select(w.id);
    if (w.locked) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { kind: handle ? 'resize' : 'move', id: w.id, handle, sx: e.clientX, sy: e.clientY, start: { x: w.x, y: w.y, w: w.w, h: w.h }, moved: false };
    useStudio.getState().beginDrag();
    setActive(w.id);
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / scale, dy = (e.clientY - d.sy) / scale;
    if (!d.moved && Math.hypot(dx * scale, dy * scale) < 3) return;
    d.moved = true;
    const cx = Math.round(dx / stepX), cy = Math.round(dy / stepY);
    const w = useStudio.getState().design!.widgets.find((x) => x.id === d.id);
    if (!w) return;
    const min = minSize(w, design.cols, design.rows);
    let r = d.kind === 'move'
      ? clampRect({ ...d.start, x: d.start.x + cx, y: d.start.y + cy }, design.cols, design.rows, min)
      : resizeRect(d.start, d.handle!, cx, cy, design.cols, design.rows, min);
    if (d.kind === 'resize') {
      // Fixed-shape widgets (posters): the side being dragged leads, the other follows; the opposite edges stay put.
      const h = d.handle!;
      const drive = h === 'e' || h === 'w' ? 'w' : h === 'n' || h === 's' ? 'h' : Math.abs(cx) * stepX >= Math.abs(cy) * stepY ? 'w' : 'h';
      r = keepShape(w, r, design, drive, { x: h.includes('w') ? 'end' : 'start', y: h.includes('n') ? 'end' : 'start' });
    }
    if (r.x !== w.x || r.y !== w.y || r.w !== w.w || r.h !== w.h) {
      useStudio.getState().preview((x) => ({ ...x, widgets: x.widgets.map((y) => (y.id === d.id ? { ...y, ...r } : y)) }));
    }
  };
  const onUp = () => {
    if (!drag.current) return;
    drag.current = null;
    setActive(null);
    useStudio.getState().endDrag();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && drag.current) { drag.current = null; setActive(null); useStudio.getState().cancelDrag(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const sel = design.widgets.find((w) => w.id === selected && !w.hidden);
  return (
    <div className="ov" style={{ ['--inv' as string]: inv }} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      onPointerDown={(e) => { if (e.target === e.currentTarget) select(null); }}>
      {dots && <div className="ov-dots" aria-hidden style={{ backgroundSize: `${stepX}px ${stepY}px`, backgroundPosition: `${m.margin - m.gap / 2 - stepX / 2}px ${m.margin - m.gap / 2 - stepY / 2}px` }} />}
      <div className="ov-safe" aria-hidden style={{ inset: m.margin }} />
      {design.widgets.map((w) => {
        if (w.hidden) return null;
        const px = rectToPx(w, m);
        return (
          <div key={w.id} className={`ov-hit${w.style === 'none' ? ' plain' : ''}${w.locked ? ' locked' : ''}${w.id === selected ? ' sel' : ''}${picking && picked.includes(w.id) ? ' picked' : ''}${active === w.id ? ' dragging' : ''}`}
            style={{ left: px.left, top: px.top, width: px.width, height: px.height, borderRadius: cornerRadius(w, m) }}
            onPointerDown={(e) => onDown(e, w)} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
            role="button" aria-label={`${WIDGETS[w.type].label}, ${w.w} by ${w.h} cells${w.locked ? ', locked' : ''}`} aria-pressed={picking ? picked.includes(w.id) : w.id === selected} tabIndex={-1} />
        );
      })}
      {sel && (() => {
        const px = rectToPx(sel, m);
        return (
          <div className={`ov-sel${sel.locked ? ' locked' : ''}${sel.y === 0 ? ' top' : ''}`} style={{ left: px.left, top: px.top, width: px.width, height: px.height }}>
            <span className="ov-badge mono">{sel.w} × {sel.h}{sel.locked ? ' · Locked' : ''}</span>
            {!sel.locked && HANDLES.map((h) => (
              <span key={h} className={`ov-h h-${h}`} onPointerDown={(e) => onDown(e, sel, h)} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} aria-hidden />
            ))}
          </div>
        );
      })()}
      {ghost && dnd.type && (() => {
        const px = rectToPx(ghost, m);
        return (
          <div className="ov-drop" style={{ left: px.left, top: px.top, width: px.width, height: px.height, borderRadius: m.radius }}>
            <Icon name="plus" size={Math.round(40 * Math.min(2, inv))} />
            <b>{WIDGETS[dnd.type].label}</b>
            <small>Release to place · {ghost.w} × {ghost.h}</small>
          </div>
        );
      })()}
    </div>
  );
}
